import './style.scss';
import {
  TextInput,
  Button,
  BackArrowIcon,
  SwitchToggleInput,
  BooleanInput,
  AutoCompleteSearch,
  HorizontalLine,
  required,
  urlRule,
  emailRules,
  postalCodeRule,
  streetRule,
  floorSuiteRule,
  useForm,
  TruForm,
  FormArray,
  companyRules,
  ExcelImportTagsInput,
  MultiSelect,
} from '@trufla-technology/trumarket-react-design-system';
import {
  useListCompanyContacts,
  useGetCompany,
} from '@trufla-technology/trufla-trudomain/dist/lib/truMarket';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import {
  GetLocationByKey,
  GetCompanyContactByKey,
  GetCompanyPhoneByKey,
} from '@trufla-technology/trufla-trudomain/dist/lib/truMarket/types';
import { PrimaryContactSection } from './primaryContact';
import { SocialMediaSection } from './socialMedia';
import { TruFormItem } from '@trufla-technology/trumarket-react-design-system/lib/components/widgets/TruForm/TruFormItem';
import { GoogleMapView } from '@trufla-technology/trumarket-react-design-system';
import {
  BranchFormType,
  LatLng,
  NamePath,
  days,
  defaultDays,
} from 'app/types/Branch';
import { useBranchForm } from './useBranchForm.service';
import {
  OptionsType,
  ValueType,
} from '@trufla-technology/trumarket-react-design-system/lib/components/controls/MultiSelect';
import { ADDRESS_ZONE_REGEX } from 'app/utils/Regex';
import { customPhoneRules } from './phoneValidation';

type Props = {
  mode?: string;
  initialData?: GetLocationByKey;
  contactsData?: GetCompanyContactByKey[];
  phonesData?: GetCompanyPhoneByKey[];
  branchKey?: string;
};

//TODO: to be added and retrieved from RDS
const zoneRules = [
  {
    pattern: ADDRESS_ZONE_REGEX,
    message: 'Must be alpha chars only',
  },
];

export const BranchForm = ({
  initialData,
  mode,
  contactsData,
  phonesData,
  branchKey,
}: Props) => {
  let [regex, min, max] = companyRules;
  regex = { ...regex, pattern: /^[a-zA-Z0-9@!._,'\\-\s]*$/ };
  const companyNameRules = [regex, min, max];

  const contractNumberRegex = /^(?:[A-Z]{2,3}-)?\d{6,10}(?:-[A-Z0-9]{1,3})?$/;
  const { data: ListCompanyContacts } = useListCompanyContacts({});
  const { data: CompanyData } = useGetCompany();
  const [address, setAddress] = useState<string>('');
  const [addressValidation, setAddressValidation] = useState(false);
  const [primaryContactValidation, setPrimaryContactValidation] =
    useState(false);

  const [latLng, setLatLng] = useState<LatLng>({
    lat: +initialData?.latitude!!,
    lng: +initialData?.longitude!!,
  } as google.maps.LatLngLiteral);

  const mergedWorkingDays = defaultDays.map((day) => {
    const objToFind = initialData?.workingHours?.find(
      (obj) => obj.day === day.day,
    );
    if (objToFind) return { ...objToFind, checkDay: true };
    else return day;
  });

  const initialFormData = initialData
    ? {
        ...initialData,
        // @ts-ignore until we update truDomain
        codes: initialData?.codes?.map((code: string) => {
          return {
            label: code?.toUpperCase(),
            value: code,
          } as OptionsType[number];
        }) as ValueType,
        contractNumbers: initialData?.contractNumbers,
        mailboxes:
          initialData?.mailboxes && initialData?.mailboxes?.length > 0
            ? [initialData.mailboxes[0]]
            : [{ type: 'support', mail: '' }],
        useGlobalWebsite: initialData?.website
          ? false
          : CompanyData?.website !== undefined,
        website: initialData?.website ?? CompanyData?.website,
        useGlobalPrimaryContact:
          contactsData && contactsData.length > 0 && contactsData[0]?.name
            ? false
            : true,
        useGlobalSocialMedia: initialData?.socialMedia?.facebook ? false : true,
        phones:
          phonesData && phonesData?.length > 0
            ? phonesData?.map((phone) => {
                return { number: phone.number, type: phone.type };
              })
            : [{ number: '', type: 'Phone' }],
        workingHours: mergedWorkingDays as any,
        'address.street': initialData?.address?.street,
        'address.suite': initialData?.address?.suite,
        'address.country': initialData?.address?.country,
        'address.floor': initialData?.address?.floor,
        'address.unitNumber': initialData?.address?.unitNumber as string,
        'address.city': initialData?.address?.city,
        'address.postalCode': initialData?.address?.postalCode,
        'address.province': initialData?.address?.province,
        'address.fullAddress': initialData?.address?.fullAddress,
        longitude: +initialData?.longitude!,
        latitude: +initialData?.latitude!,
        'socialMedia.facebook': initialData?.socialMedia?.facebook,
        'socialMedia.twitter': initialData?.socialMedia?.twitter,
        'socialMedia.linkedIn': initialData?.socialMedia?.linkedIn,
        'socialMedia.youtube': initialData?.socialMedia?.youtube,
      }
    : {
        useGlobalWebsite: CompanyData?.website !== undefined,
        useGlobalPrimaryContact: false,
        useGlobalSocialMedia: CompanyData?.socialMedia !== undefined,
        website: CompanyData?.website,
        phones: [{ number: '', type: 'Phone' }],
      };

  const form = useForm<BranchFormType>(initialFormData as any);

  const { update, values } = form;
  const {
    handleFinish,
    handleSelectAddress,
    history,
    getAddress,
    toaster,
    branchCodes,
    loading,
  } = useBranchForm(
    values,
    update,
    setLatLng,
    setAddress,
    latLng,
    branchKey,
    mode,
    phonesData,
    contactsData,
    setAddressValidation,
    setPrimaryContactValidation,
    form,
  );

  useEffect(() => {
    const formValues = values();

    if (formValues.useGlobalWebsite && CompanyData) {
      update({
        ...formValues,
        website: CompanyData.website,
      });
    } else if (!formValues.useGlobalWebsite && CompanyData && mode !== 'edit') {
      update({
        ...formValues,
        website: '',
      });
    } else if (!formValues.useGlobalWebsite && CompanyData && mode === 'edit') {
      update({
        ...formValues,
        website: initialData?.website,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CompanyData, values().useGlobalWebsite, update, mode, initialData]);

  useEffect(() => {
    const formValues = values();
    if (
      formValues.useGlobalPrimaryContact &&
      ListCompanyContacts &&
      ListCompanyContacts[0]
    ) {
      update({
        ...formValues,
        'primaryContact.name': ListCompanyContacts[0].name,
        'primaryContact.phones': ListCompanyContacts[0].phones,
        'primaryContact.title': ListCompanyContacts[0].title,
        'primaryContact.email': ListCompanyContacts[0].email,
      });
    } else if (
      (!formValues.useGlobalPrimaryContact &&
        ListCompanyContacts &&
        mode !== 'edit') ||
      (!formValues.useGlobalPrimaryContact &&
        ListCompanyContacts &&
        contactsData?.length === 0 &&
        mode === 'edit')
    ) {
      update({
        ...formValues,
        'primaryContact.name': '',
        'primaryContact.phones': [{ type: '', number: '' }],
        'primaryContact.title': '',
        'primaryContact.email': '',
      });
    } else if (
      !formValues.useGlobalPrimaryContact &&
      ListCompanyContacts &&
      contactsData &&
      mode === 'edit'
    ) {
      update({
        ...formValues,
        'primaryContact.name': contactsData[0].name,
        'primaryContact.phones': contactsData[0].phones,
        'primaryContact.title': contactsData[0].title,
        'primaryContact.email': contactsData[0].email,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    values().useGlobalPrimaryContact,
    ListCompanyContacts,
    update,
    mode,
    contactsData,
    form.values,
  ]);

  useEffect(() => {
    const formValues = values();
    if (formValues.useGlobalSocialMedia && CompanyData) {
      update({
        ...formValues,
        'socialMedia.facebook': CompanyData.socialMedia?.facebook,
        'socialMedia.twitter': CompanyData.socialMedia?.twitter,
        'socialMedia.linkedIn': CompanyData.socialMedia?.linkedIn,
        'socialMedia.youtube': CompanyData.socialMedia?.youtube,
      });
    } else if (
      !formValues.useGlobalSocialMedia &&
      CompanyData &&
      mode !== 'edit'
    ) {
      update({
        ...formValues,
        'socialMedia.facebook': '',
        'socialMedia.twitter': '',
        'socialMedia.linkedIn': '',
        'socialMedia.youtube': '',
      });
    } else if (
      !formValues.useGlobalSocialMedia &&
      CompanyData &&
      initialData &&
      mode === 'edit'
    ) {
      update({
        ...formValues,
        'socialMedia.facebook': initialData.socialMedia?.facebook,
        'socialMedia.twitter': initialData.socialMedia?.twitter,
        'socialMedia.linkedIn': initialData.socialMedia?.linkedIn,
        'socialMedia.youtube': initialData.socialMedia?.youtube,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [CompanyData, values().useGlobalSocialMedia, update, mode, initialData]);

  const [, setState] = useState({
    useGlobalSocialMedia: false,
    useGlobalPrimaryContact: false,
    useGlobalWebSite: CompanyData?.website !== undefined,
  });

  const optionList = ['Phone', 'Cell', 'Fax'];
  const updateGlobalField = (key: string, value: boolean) => {
    update({
      ...values(),
      [key]: value,
    });
    setState((prev) => {
      return {
        ...prev,
        key: value,
      };
    });
  };

  const getCodesDefaultValue = () => {
    if ((values().codes as ValueType)?.[0].value) {
      return values().codes as ValueType;
    } else if (typeof values().codes?.[0] === 'string') {
      const newValues = values().codes?.map((code) => {
        return {
          label: (code as string)?.toUpperCase(),
          value: code,
        } as OptionsType[number];
      }) as ValueType;
      return newValues;
    } else {
      return null;
    }
  };

  return (
    <div className="branch-form-page">
      <div className="form">
        <TruForm onFinish={handleFinish} form={form}>
          <div className="header-branch-form">
            <Link
              className="flex-div back-button"
              to="/configurations/company-details/"
            >
              <BackArrowIcon width="20px" />
              <p className="back">Back</p>
            </Link>
            <div className="flex-div">
              <p>Set As Default Branch </p>
              <div>
                <form.Item name="headquarter">
                  <SwitchToggleInput />
                </form.Item>
              </div>
            </div>
          </div>
          <h2>{mode === 'edit' ? 'Edit' : 'Add'} Branch</h2>
          <div className="">
            <div className="inputs-wrraper">
              <form.Item
                name="name"
                label="Branch Name"
                rules={[...companyNameRules, required]}
              >
                <TextInput />
              </form.Item>
              <div className="website-section">
                <p>Website</p>
                <div className="flex-div">
                  <div className="flex-div">
                    <span className="pr-1 use-global">Use Company's</span>
                    <form.Item name="useGlobalWebsite">
                      <SwitchToggleInput
                        disabled={!CompanyData?.website}
                        onChange={(v) => {
                          updateGlobalField('useGlobalWebsite', v);
                        }}
                      />
                    </form.Item>
                  </div>

                  <form.Item name="website" rules={[...urlRule]}>
                    <TextInput disabled={values().useGlobalWebsite} />
                  </form.Item>
                </div>
              </div>
              <form.Item className="codes" name="codes" label="BMS Branch Code">
                <MultiSelect
                  defaultValue={getCodesDefaultValue()}
                  asValueArray
                  options={branchCodes || []}
                  showCaretUpAndDownIcon
                />
              </form.Item>
              <form.Item name="contractNumbers" label="Contract Numbers">
                <ExcelImportTagsInput
                  value={values().contractNumbers}
                  searchColumnName="phone"
                  validatePattern={contractNumberRegex}
                  onShowToaster={(message, type) => {
                    toaster[type](message);
                  }}
                  helpMessage="Add contract numbers followed by enter↵ to add new one, or copy and paste from Excel or CSV file."
                />
              </form.Item>
            </div>
            <p className="title">Contact Details</p>
            <div className="inputs-wrraper address-wrapper">
              <FormArray
                Form={form}
                optionList={optionList}
                name="phones"
                typeLabel="Phone"
                buttonType="button"
                rules={
                  initialData?.['phones']?.[0]?.number
                    ? [...customPhoneRules, required]
                    : [...customPhoneRules]
                }
              ></FormArray>
              <form.List
                name="mailboxes"
                initialValue={[{ type: 'support', mail: '' }]}
              >
                {(fields) => (
                  <>
                    {fields.map((field, index) => (
                      <div className="" key={field.key}>
                        <div className="hidden">
                          <form.Item
                            {...field}
                            name={
                              [field.name, 'type'] as NamePath &
                                keyof BranchFormType
                            }
                            label="type"
                            initialValue={'support'}
                          >
                            <TextInput />
                          </form.Item>
                        </div>
                        <form.Item
                          {...field}
                          rules={[...emailRules]}
                          name={
                            [field.name, 'mail'] as NamePath &
                              keyof BranchFormType
                          }
                          label="Email"
                        >
                          <TextInput />
                        </form.Item>
                      </div>
                    ))}
                  </>
                )}
              </form.List>
              <TruFormItem
                name="address.fullAddress"
                label="Address (Used for location)"
              >
                <AutoCompleteSearch
                  value={address}
                  onChange={(value) => {
                    setAddress(value);
                  }}
                  onSelectData={(e) => {
                    handleSelectAddress(e);
                  }}
                />
              </TruFormItem>
              <div className="flex-100">
                <div className="flex-50">
                  <form.Item
                    name="address.country"
                    label="Country"
                    rules={addressValidation ? [required] : []}
                  >
                    <TextInput />
                  </form.Item>
                  <form.Item
                    name="address.city"
                    label="City / state"
                    rules={addressValidation ? [required] : []}
                  >
                    <TextInput />
                  </form.Item>
                  <form.Item
                    name="address.province"
                    label="Province / Zone"
                    rules={zoneRules}
                  >
                    <TextInput />
                  </form.Item>
                  <form.Item
                    name="address.postalCode"
                    label="Postal Code"
                    rules={
                      addressValidation
                        ? [...postalCodeRule, required]
                        : [...postalCodeRule]
                    }
                  >
                    <TextInput />
                  </form.Item>
                  <form.Item
                    name="address.street"
                    label="Street"
                    rules={
                      addressValidation
                        ? [...streetRule, required]
                        : [...streetRule]
                    }
                  >
                    <TextInput />
                  </form.Item>
                  <form.Item name="address.unitNumber" label="Building">
                    <TextInput />
                  </form.Item>
                  <form.Item
                    name="address.floor"
                    label="Floor"
                    rules={floorSuiteRule}
                  >
                    <TextInput />
                  </form.Item>
                  <form.Item
                    name="address.suite"
                    label="Suite Number"
                    rules={floorSuiteRule}
                  >
                    <TextInput />
                  </form.Item>
                </div>
                <div className="flex-50 map-container">
                  <GoogleMapView
                    lat={latLng?.lat}
                    lng={latLng?.lng}
                    onLocationSelection={(location: any) =>
                      getAddress(location)
                    }
                  />
                </div>
              </div>
            </div>

            <PrimaryContactSection
              changeValue={updateGlobalField}
              disabled={values().useGlobalPrimaryContact!!}
              form={form}
              primaryContactValidation={primaryContactValidation}
            />
            <SocialMediaSection
              changeValue={updateGlobalField}
              disabled={values().useGlobalSocialMedia!!}
              form={form}
            />
            <p className="title">Working Hours</p>
            <div className="inputs-wrraper">
              <form.List
                name="workingHours"
                initialValue={[
                  { day: 'SAT' },
                  { day: 'SUN' },
                  { day: 'MON' },
                  { day: 'TUES' },
                  { day: 'WED' },
                  { day: 'THUR' },
                  { day: 'FRI' },
                ]}
              >
                {(fields) => (
                  <>
                    {fields.map((field, index) => (
                      <div className="working-days-row" key={field.key}>
                        <div className="day">
                          <div className="checkbox-sec">
                            <form.Item
                              {...field}
                              name={
                                [field.name, 'checkDay'] as NamePath &
                                  keyof BranchFormType
                              }
                            >
                              <BooleanInput />
                            </form.Item>
                            <span>{days[index]}</span>
                          </div>
                        </div>
                        <div className="time">
                          <div className="working-hours ">
                            <span>From</span>
                            <form.Item
                              {...field}
                              name={
                                [field.name, 'from'] as NamePath &
                                  keyof BranchFormType
                              }
                              rules={[
                                {
                                  pattern: new RegExp(
                                    /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/,
                                  ),
                                  message: 'ex:HH:MM',
                                },
                              ]}
                            >
                              <TextInput />
                            </form.Item>
                          </div>
                          <div className="working-hours ">
                            <span>To</span>
                            <form.Item
                              {...field}
                              name={
                                [field.name, 'to'] as NamePath &
                                  keyof BranchFormType
                              }
                              rules={[
                                {
                                  pattern: new RegExp(
                                    /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/,
                                  ),
                                  message: 'ex:HH:MM',
                                },
                              ]}
                            >
                              <TextInput />
                            </form.Item>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </form.List>
            </div>
          </div>
          <HorizontalLine verticalMargin="25px" />
          <Button type="primary" htmlType="submit" loading={loading}>
            Submit
          </Button>
          <Button
            type="secondary"
            htmlType="button"
            onClick={() => {
              history.push('/configurations/company-details/');
            }}
          >
            Cancel
          </Button>
        </TruForm>
      </div>
    </div>
  );
};
